import React, { useEffect, useState } from "react";
import Text from "@/components/ui/text";
import { cloneDeep, isEmpty, isNumber, uniqueId } from "lodash";
import Modal from "@/components/controls/Modal";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { Period as PeriodType } from "@/pages/deals/types";
import Period from "@/pages/deals/components/commercials/ContractTerms/Period";
import { formatNumber } from "@/utils/numbers";
import { getCurrentLocale } from "@/utils/locale";
import { Add } from "@dreamhub/designsystem/icons";
import ConvertedCurrency from "@/pages/deals/components/commercials/ConvertedCurrency";

const TRANSLATION_PREFIX = "deals.commercials.amountBreakdownModal";

const createEmptyPeriod = () => ({
  id: uniqueId(),
  startMonth: 1,
  endMonth: null,
  amount: null
});

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  periodsBreakdown: PeriodType[] | null;
  handleSubmit: (periods: PeriodType[]) => void;
  showConversion: boolean;
  contractCurrencySymbol: string;
  totalAmount: number;
  contractDurationInMonths: number | null;
  rate: number | undefined;
  accountCurrencySymbol: string;
}

const AmountBreakdownModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  periodsBreakdown,
  handleSubmit,
  showConversion,
  contractCurrencySymbol,
  totalAmount,
  contractDurationInMonths,
  rate,
  accountCurrencySymbol
}) => {
  const { t } = useTranslation();

  const [periods, setPeriods] = useState<PeriodType[]>(() => {
    return periodsBreakdown
      ? cloneDeep(periodsBreakdown)
      : [createEmptyPeriod()];
  });

  const [globalFieldErrors, setGlobalFieldErrors] = useState<{
    [periodFieldKey: string]: string;
  }>({});

  const [disableSubmit, setDisableSubmit] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setPeriods(
        periodsBreakdown ? cloneDeep(periodsBreakdown) : [createEmptyPeriod()]
      );
      setGlobalFieldErrors({});
    }
  }, [isOpen, periodsBreakdown]);

  useEffect(() => {
    const hasErrors = Object.keys(globalFieldErrors).length > 0;
    const hasNullFields = periods.some((period) =>
      Object.values(period).some((value) => value === null)
    );

    if (hasErrors || hasNullFields) {
      return setDisableSubmit(true);
    }
    setDisableSubmit(false);
  }, [periods, globalFieldErrors]);

  const handleInputChange = (
    periodId: string,
    fieldName: keyof PeriodType,
    newValue: string,
    error: boolean,
    errorMessage?: string
  ) => {
    const fieldKey = `${periodId}-${fieldName}`;
    setGlobalFieldErrors((prev) => {
      const newErrors = { ...prev };
      if (error && errorMessage) {
        newErrors[fieldKey] = errorMessage;
      } else {
        delete newErrors[fieldKey];
      }
      return newErrors;
    });

    const numValue = Number(newValue);
    if (isNaN(numValue)) return;
    const periodIndex = periods.findIndex((p) => p.id === periodId);
    if (periodIndex === -1) return;
    const newPeriods = periods.map((p) => ({ ...p }));
    newPeriods[periodIndex] = {
      ...newPeriods[periodIndex],
      [fieldName]: numValue
    } as PeriodType;
    if (fieldName === "endMonth" && periodIndex < newPeriods.length - 1) {
      newPeriods[periodIndex + 1] = {
        ...newPeriods[periodIndex + 1],
        startMonth: numValue + 1
      };
    }
    setPeriods(newPeriods);
  };

  const handleAddPeriod = (previousPeriod: PeriodType) => {
    const periodIndex = periods.findIndex((p) => p.id === previousPeriod.id);
    if (periodIndex === -1) return;
    const newPeriod: PeriodType = {
      id: uniqueId(),
      startMonth: isNumber(previousPeriod.endMonth)
        ? previousPeriod.endMonth + 1
        : null,
      endMonth: null,
      amount: null
    };
    const newPeriods = [...periods];
    newPeriods.splice(periodIndex + 1, 0, newPeriod);
    setPeriods(newPeriods);
  };

  const handleDeletePeriod = (period: PeriodType) => {
    const periodIndex = periods.findIndex((p) => p.id === period.id);
    if (periodIndex === -1) return;

    // Clean up field errors for the deleted period
    const updatedErrors = { ...globalFieldErrors };
    Object.keys(updatedErrors).forEach((key) => {
      if (key.startsWith(`${period.id}-`)) {
        delete updatedErrors[key];
      }
    });
    setGlobalFieldErrors(updatedErrors);

    const newPeriods = [...periods];
    if (periodIndex > 0 && periodIndex < newPeriods.length - 1) {
      const previousPeriod = newPeriods[periodIndex - 1];
      if (isNumber(previousPeriod.endMonth)) {
        newPeriods[periodIndex + 1] = {
          ...newPeriods[periodIndex + 1],
          startMonth: previousPeriod.endMonth + 1
        };
      }
    }
    newPeriods.splice(periodIndex, 1);
    setPeriods(newPeriods);
  };

  const getFormattedTotalAmount = () => {
    const { value: formattedValue, currencySign } = formatNumber(
      totalAmount,
      0,
      true,
      false,
      false,
      getCurrentLocale()
    );
    return `${contractCurrencySymbol || currencySign}${formattedValue}`;
  };

  const lastPeriod = periods[periods.length - 1];

  return (
    <Modal
      open={isOpen}
      setIsOpen={setIsOpen}
      headerClassname="p-6 pl-5"
      contentClassname="w-[40rem]"
      title={
        <div className="flex flex-col gap-2">
          <Text as="div" variant="lgSubtitleH1">
            {t(`${TRANSLATION_PREFIX}.title`)}
          </Text>
          <Text variant="lgBodySecondary">
            {t(`${TRANSLATION_PREFIX}.subtitle`)}
          </Text>
        </div>
      }
      content={
        <div className="flex flex-col h-fit p-6 gap-4">
          {periods.map((period, index) => (
            <Period
              key={period.id}
              period={period}
              showTitle={index === 0}
              tPrefix={TRANSLATION_PREFIX}
              showConversion={showConversion}
              contractCurrencySymbol={contractCurrencySymbol}
              onInputChange={handleInputChange}
              onAddPeriod={handleAddPeriod}
              onDeletePeriod={handleDeletePeriod}
              contractDurationInMonths={contractDurationInMonths}
              rate={rate}
              accountCurrencySymbol={accountCurrencySymbol}
              totalAmount={totalAmount}
              allPeriodsAmounts={periods
                .filter((p) => p.id !== period.id)
                .reduce((sum, p) => sum + (p.amount || 0), 0)}
              isOnlyPeriod={periods.length === 1}
            />
          ))}
          <Button
            variant="tertiary"
            className="w-fit flex gap-1 -translate-x-2"
            onClick={() => handleAddPeriod(lastPeriod)}
            disabled={
              !lastPeriod.endMonth ||
              !isEmpty(globalFieldErrors) ||
              lastPeriod.endMonth === contractDurationInMonths
            }
          >
            <Add />
            <span>{t(`${TRANSLATION_PREFIX}.addPeriod`)}</span>
          </Button>
        </div>
      }
      footer={
        <div className="flex w-full justify-between items-center">
          <div className="flex flex-col gap-[2px]">
            <Text variant="bodySecondary">
              {t(`${TRANSLATION_PREFIX}.totalAmount`)}
            </Text>
            <div className="flex gap-2 items-end">
              <Text variant="label">{getFormattedTotalAmount()}</Text>
              {showConversion && (
                <ConvertedCurrency
                  amount={totalAmount}
                  rate={rate}
                  accountCurrencySymbol={accountCurrencySymbol}
                />
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              data-test="cancel"
              variant="secondary"
              onClick={() => setIsOpen(false)}
            >
              {t("cancel")}
            </Button>
            <Button
              data-test="save-breakdown"
              onClick={() => handleSubmit(periods)}
              disabled={disableSubmit}
            >
              {t(`${TRANSLATION_PREFIX}.save`)}
            </Button>
          </div>
        </div>
      }
    />
  );
};

export default AmountBreakdownModal;
